import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api, queryString } from '@/lib/api';
import type { StatusTone } from '@/components/ui/status-badge';
import type {
  ConversationAssignee,
  ConversationDetail,
  ConversationListItem,
  ConversationStatusValue,
  StartConversationInput,
  StartConversationResult,
} from '@shared/schemas/conversation';
import type { ListResponse, RecordSort } from '@shared/schemas/pagination';

/* The queries behind /admin/conversations. ROADMAP.md §9.3 to §9.6. */

const ROOT = ['admin', 'conversations'] as const;

/**
 * The badge tone per state — one mapping, so no screen invents its own.
 *
 * `waiting_us` is the loud one on purpose: it is the only state that means
 * somebody is waiting on Honeycomb, which is the state the office should be
 * emptying. `closed` is grey rather than green — a finished conversation is a
 * normal outcome, not an achievement.
 */
export const CONVERSATION_TONES: Record<ConversationStatusValue, StatusTone> = {
  open: 'pending',
  waiting_customer: 'scheduled',
  waiting_us: 'in-progress',
  closed: 'cancelled',
};

function invalidate(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ROOT }).then(() => undefined);
}

export interface ConversationListParams {
  sort: RecordSort;
  page: number;
  status: string;
  search: string;
  assignedTo: string;
}

export function useConversations(params: ConversationListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<ListResponse<ConversationListItem>>(
        `/api/admin/conversations${queryString({
          page: params.page,
          sort: params.sort,
          status: params.status,
          search: params.search === '' ? undefined : params.search,
          assignedTo: params.assignedTo === '' ? undefined : params.assignedTo,
        })}`,
      ),
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: [...ROOT, id],
    queryFn: () => api.get<ConversationDetail>(`/api/admin/conversations/${id}`),
    enabled: id !== '',
  });
}

export function useConversationAssignees() {
  return useQuery({
    queryKey: [...ROOT, 'assignees'],
    queryFn: () =>
      api.get<{ assignees: ConversationAssignee[] }>('/api/admin/conversations/assignees'),
  });
}

/**
 * Status, assignment and spam, all shaped the same way.
 *
 * Each returns the whole updated thread, so the detail pane is correct straight
 * away without a second round trip — and the list is invalidated because the
 * row's badge, assignee and position all just changed.
 */
export function useConversationStatus(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: ConversationStatusValue) =>
      api.patch<ConversationDetail>(`/api/admin/conversations/${id}/status`, { status }),
    onSuccess: async (detail) => {
      queryClient.setQueryData([...ROOT, id], detail);
      await invalidate(queryClient);
    },
  });
}

export function useConversationAssignment(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignedToUserId: string | null) =>
      api.patch<ConversationDetail>(`/api/admin/conversations/${id}/assignment`, {
        assignedToUserId,
      }),
    onSuccess: async (detail) => {
      queryClient.setQueryData([...ROOT, id], detail);
      await invalidate(queryClient);
    },
  });
}

export function useConversationSpam(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isSpam: boolean) =>
      api.patch<ConversationDetail>(`/api/admin/conversations/${id}/spam`, { isSpam }),
    onSuccess: async (detail) => {
      queryClient.setQueryData([...ROOT, id], detail);
      await invalidate(queryClient);
    },
  });
}

/**
 * Open a thread with a customer or an enquiry and send the first message.
 *
 * **Invalidates everything under the conversations root, including the
 * per-record panels**, because a brand-new thread has to appear on the
 * customer's record and in the inbox at once — there is no existing cache entry
 * to patch, which is what the status and assignment mutations above do instead.
 */
export function useStartConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: StartConversationInput) =>
      api.post<StartConversationResult>('/api/admin/conversations', input),
    onSuccess: () => invalidate(queryClient),
  });
}

/**
 * Keep the open thread and the inbox current without a refresh — ROADMAP §9.6.
 *
 * **`EventSource`, not a poll and not a socket.** It reconnects on its own with
 * the last event id it saw, which is what makes the server's replay work; a
 * poll would either be too slow to feel live or too chatty to be free.
 *
 * The stream carries *what changed*, not the change itself, and this hook
 * invalidates rather than patching the cache from the payload. That is
 * deliberate: a payload the browser trusts is a second copy of the truth, and
 * the moment it disagrees with the server the screen is confidently wrong. An
 * invalidate costs one small request and cannot drift.
 */
export function useConversationLiveUpdates(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // A same-origin stream, so no credentials juggling — the session cookie goes.
    const source = new EventSource('/api/admin/conversations/events');

    source.addEventListener('conversation', (event) => {
      let payload: { conversationId?: string };
      try {
        payload = JSON.parse((event as MessageEvent<string>).data) as { conversationId?: string };
      } catch {
        return;
      }

      // The list always: a badge, an order or a preview just moved.
      void queryClient.invalidateQueries({ queryKey: [...ROOT, 'list'] });

      // And the thread itself, if it is the one on screen.
      if (payload.conversationId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: [...ROOT, payload.conversationId] });
      }
    });

    /*
     * **No reconnect logic here on purpose.** `EventSource` retries by itself,
     * with backoff, sending `Last-Event-ID`. Adding our own would fight it and
     * would be the thing that opens two streams per tab.
     */
    return () => {
      source.close();
    };
  }, [queryClient]);
}
